import {
  addToast,
  Button,
  Card,
  CardBody,
  CardHeader,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Tooltip,
} from '@heroui/react'
import { formatDistanceToNow } from 'date-fns'
import {
  AlertTriangle,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  Copy,
  ExternalLink,
  File,
  FolderMinus,
  Globe,
  Loader2,
  MessageCircle,
  MoreHorizontal,
  Pencil,
  Trash2,
} from 'lucide-react'
import { useState } from 'react'
import placeHolderImage from '../../assets/svg/placeholder_file.svg'
import DeleteKnowledgeBase from './DeleteKnowledgeBase'
import EditKnowledgeBase from './EditKnowledgeBase'

export default function KnowledgeBaseCards({
  data,
  onRefresh,
  globalFilter,
  onTestKnowledgeBase,
}) {
  const [knowledgeBase, setKnowledgeBase] = useState(null)
  const [openEdit, setOpenEdit] = useState(false)
  const [openDelete, setOpenDelete] = useState(false)
  const [expandedUrls, setExpandedUrls] = useState({})

  // Helper function to parse source URLs safely
  const parseSourceUrls = (sourceUrlsString) => {
    if (!sourceUrlsString)
      return []
    try {
      return JSON.parse(sourceUrlsString)
    }
    catch (error) {
      console.error('Failed to parse source URLs:', error)
      return []
    }
  }

  // Helper function to get type information
  const getTypeInfo = (type) => {
    switch (type) {
      case 'WEB_SCRAPE':
        return {
          icon: <Globe className="h-3 w-3 text-blue-600" />,
          label: 'Web Scrape',
          bgColor: 'bg-blue-50',
          textColor: 'text-blue-700',
          borderColor: 'border-blue-200',
        }
      case 'FILE':
      default:
        return {
          icon: <File className="h-3 w-3 text-gray-600" />,
          label: 'File',
          bgColor: 'bg-gray-50',
          textColor: 'text-gray-700',
          borderColor: 'border-gray-200',
        }
    }
  }

  const toggleUrlExpansion = (itemId) => {
    setExpandedUrls(prev => ({
      ...prev,
      [itemId]: !prev[itemId],
    }))
  }

  const handleCopyKbId = (id) => {
    const kbId = `KB-${id}`
    navigator.clipboard.writeText(kbId)
    addToast({
      title: 'Knowledge Base ID copied to clipboard',
      description: kbId,
      color: 'success',
      duration: 3000,
    })
  }

  // Helper function to get status information
  const getStatusInfo = (status) => {
    switch (status) {
      case 'PROCESSING':
        return {
          icon: <Loader2 className="h-6 w-6 animate-spin text-blue-600" />,
          text: 'Processing (~few mins)',
          bgColor: 'bg-blue-100',
          textColor: 'text-blue-800',
          borderColor: 'border-blue-300',
          showBlur: true,
        }
      case 'FAILED':
        return {
          icon: <AlertTriangle className="h-4 w-4 text-red-600" />,
          text: 'Failed',
          bgColor: 'bg-red-50',
          textColor: 'text-red-700',
          borderColor: 'border-red-200',
          showBlur: false,
        }
      case 'COMPLETED':
        return {
          icon: <CheckCircle className="h-4 w-4 text-green-600" />,
          text: 'Ready',
          bgColor: 'bg-green-50',
          textColor: 'text-green-700',
          borderColor: 'border-green-200',
          showBlur: false,
        }
      default:
        return {
          icon: <CheckCircle className="h-4 w-4 text-green-600" />,
          text: 'Ready',
          bgColor: 'bg-green-50',
          textColor: 'text-green-700',
          borderColor: 'border-green-200',
          showBlur: false,
        }
    }
  }

  const getFilteredItems = () => {
    let items = data?.vectors ?? []

    if (globalFilter) {
      items = items.filter(item =>
        item.name.toLowerCase().includes(globalFilter.toLowerCase())
        || item.description?.toLowerCase().includes(globalFilter.toLowerCase()),
      )
    }

    return items
  }

  const filteredItems = getFilteredItems()

  if (!filteredItems.length) {
    return (
      <div className="mt-40 flex h-full w-full flex-col items-center gap-5">
        <FolderMinus size={64} className="text-gray-800" />
        <h1 className="scroll-m-12 text-3xl font-semibold tracking-tighter">
          No knowledge bases yet.
        </h1>
        <p className="text-sm text-gray-600">Upload a file to get started.</p>
      </div>
    )
  }

  return (
    <div className="w-full min-w-0 p-6">
      <div className={`
        grid grid-cols-1 gap-6
        md:grid-cols-2
        lg:grid-cols-3
      `}
      >
        {filteredItems.map((item) => {
          const statusInfo = getStatusInfo(item.status)
          const typeInfo = getTypeInfo(item.type)
          const sourceUrls = parseSourceUrls(item.source_urls)
          const isExpanded = expandedUrls[item.id]

          return (
            <Card
              key={item.id}
              classNames={{
                base: `bg-white shadow-lg border border-gray-200 rounded-lg overflow-hidden hover:shadow-xl transition-shadow duration-200 relative ${
                  item.status === 'FAILED' ? 'border-red-300 bg-red-50' : ''
                }`,
              }}
            >
              {/* Status indicator for processing and failed items */}
              {(item.status === 'FAILED' || item.status === 'PROCESSING') && (
                <div className="absolute top-3 right-3 z-10">
                  <div className={`
                    flex items-center gap-1 rounded-full px-2 py-1
                    ${statusInfo.bgColor}
                    border
                    ${statusInfo.borderColor}
                  `}
                  >
                    {statusInfo.icon}
                    <span className={`
                      text-xs font-medium
                      ${statusInfo.textColor}
                    `}
                    >
                      {statusInfo.text}
                    </span>
                  </div>
                </div>
              )}

              <CardHeader className="p-0">
                <div className={`
                  relative flex h-[200px] w-full items-center justify-center
                  bg-[#f1f3f5]
                `}
                >
                  {/* Type indicator - small badge */}
                  <div className="absolute top-2 left-2 z-10">
                    <div className={`
                      flex items-center gap-1 rounded-full px-2 py-0.5
                      ${typeInfo.bgColor}
                      border
                      ${typeInfo.borderColor}
                    `}
                    >
                      {typeInfo.icon}
                      <span className={`
                        text-xs font-medium
                        ${typeInfo.textColor}
                      `}
                      >
                        {typeInfo.label}
                      </span>
                    </div>
                  </div>

                  <div className={`
                    absolute bottom-2 left-2 flex h-5 w-fit max-w-[60%]
                    items-center justify-start gap-1 rounded border
                    border-gray-200 bg-[#F9FAFB] px-2 py-1
                  `}
                  >
                    <Tooltip
                      content={
                        item.type === 'WEB_SCRAPE'
                          ? item.base_url || 'Web Scrape'
                          : item.file?.file_name || 'Untitled Document'
                      }
                      radius="none"
                    >
                      <p className={`
                        cursor-pointer truncate text-left text-xs text-black
                      `}
                      >
                        {item.type === 'WEB_SCRAPE'
                          ? item.base_url || 'Web Scrape'
                          : item.file?.file_name || 'Untitled Document'}
                      </p>
                    </Tooltip>
                  </div>

                  {/* Show loading state for processing items */}
                  {item.status === 'PROCESSING'
                    ? (
                        <div className={`
                          flex flex-col items-center justify-center gap-3 px-4
                        `}
                        >
                          <Loader2 className={`
                            h-12 w-12 animate-spin text-blue-600
                          `}
                          />
                          <div className="flex flex-col items-center gap-1">
                            <span className="text-sm font-medium text-blue-600">Processing your knowledge base...</span>
                            <span className="text-center text-xs text-blue-500">This may take a few minutes</span>
                          </div>
                        </div>
                      )
                    : item.status === 'FAILED'
                      ? (
                          <div className={`
                            flex flex-col items-center justify-center gap-3
                          `}
                          >
                            <AlertTriangle className="h-12 w-12 text-red-600" />
                            <span className="text-sm font-medium text-red-600">Upload Failed</span>
                            <span className={`
                              px-4 text-center text-xs text-red-500
                            `}
                            >
                              This knowledge base failed to process. Please try uploading again.
                            </span>
                          </div>
                        )
                      : (
                          // Keep original image display for all completed items
                          item.image_url
                            ? (
                                <img
                                  src={item.image_url}
                                  alt={item.name || 'Knowledge Base Item'}
                                  className={`
                                    max-h-full max-w-full object-contain
                                  `}
                                  onError={(e) => {
                                    e.target.style.display = 'none'
                                    e.target.nextSibling.style.display = 'flex'
                                  }}
                                />
                              )
                            : (
                                <div className={`
                                  flex h-full w-full items-center justify-center
                                `}
                                >
                                  <img
                                    src={placeHolderImage}
                                    alt="Placeholder"
                                    className="h-48 w-48"
                                  />
                                </div>
                              )
                        )}
                </div>
              </CardHeader>
              <CardBody className="p-6">
                <div className="flex h-full min-h-0 flex-col justify-between">
                  {/* Top content section - flexible height */}
                  <div className={`
                    relative flex w-full flex-col items-start justify-start
                    gap-1
                  `}
                  >
                    <div className={`
                      flex w-full items-center justify-between pr-1
                    `}
                    >
                      <p className={`
                        text-left text-sm font-medium
                        ${
            item.status === 'FAILED'
              ? 'text-red-700'
              : `text-zinc-900`
            }
                      `}
                      >
                        {item.name || 'Untitled Knowledge Base'}
                        {item.status === 'FAILED' && (
                          <span className={`
                            ml-2 rounded-full bg-red-100 px-2 py-1 text-xs
                            text-red-700
                          `}
                          >
                            Failed
                          </span>
                        )}
                      </p>
                      <Popover>
                        <PopoverTrigger>
                          <Button
                            isIconOnly
                            variant="light"
                            size="sm"
                            className="h-8 w-8 min-w-8"
                          >
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-32 min-w-fit">
                          <div className="p-1">
                            <button
                              onClick={() => {
                                setKnowledgeBase(item)
                                setOpenEdit(true)
                              }}
                              className={`
                                flex w-full items-center gap-2 rounded px-3
                                py-1.5 text-sm font-medium transition-colors
                                hover:bg-gray-50
                              `}
                            >
                              <Pencil className="h-3 w-3" />
                              Edit
                            </button>
                            <button
                              onClick={() => handleCopyKbId(item.id)}
                              className={`
                                flex w-full items-center gap-2 rounded px-3
                                py-1.5 text-sm font-medium transition-colors
                                hover:bg-gray-50
                              `}
                            >
                              <Copy className="h-3 w-3" />
                              Copy KB ID
                            </button>
                            <button
                              onClick={() => {
                                setKnowledgeBase(item)
                                setOpenDelete(true)
                              }}
                              className={`
                                flex w-full items-center gap-2 rounded px-3
                                py-1.5 text-sm font-medium text-red-600
                                transition-colors
                                hover:bg-red-50
                              `}
                            >
                              <Trash2 className="h-3 w-3" />
                              Delete
                            </button>
                          </div>
                        </PopoverContent>
                      </Popover>
                    </div>

                    <p className="w-full text-left text-xs text-gray-600">
                      {item.description || 'No description available for this knowledge base.'}
                    </p>

                    {/* Expandable source URLs section for web scrapes */}
                    {item.type === 'WEB_SCRAPE' && sourceUrls.length > 0 && (
                      <div className="mt-2 w-full">
                        <button
                          onClick={() => toggleUrlExpansion(item.id)}
                          className={`
                            flex items-center gap-1 text-xs font-medium
                            text-blue-600 transition-colors
                            hover:text-blue-800
                          `}
                        >
                          <Globe className="h-3 w-3" />
                          {sourceUrls.length}
                          {' '}
                          scraped page
                          {sourceUrls.length !== 1 ? 's' : ''}
                          {isExpanded
                            ? (
                                <ChevronUp className="h-3 w-3" />
                              )
                            : (
                                <ChevronDown className="h-3 w-3" />
                              )}
                        </button>

                        {isExpanded && (
                          <div className={`
                            mt-2 max-h-32 overflow-y-auto border-l-2
                            border-blue-100 pl-3
                          `}
                          >
                            <div className="space-y-1">
                              {sourceUrls.map((url, index) => (
                                <div
                                  key={index}
                                  className="flex items-center gap-1"
                                >
                                  <ExternalLink className={`
                                    h-2.5 w-2.5 flex-shrink-0 text-gray-400
                                  `}
                                  />
                                  <Tooltip content={url}>
                                    <a
                                      href={url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className={`
                                        block max-w-full truncate text-xs
                                        text-blue-600
                                        hover:text-blue-800
                                      `}
                                    >
                                      {url}
                                    </a>
                                  </Tooltip>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Bottom section - always stays at bottom */}
                  <div className={`
                    mt-4 flex w-full items-end justify-between gap-1
                  `}
                  >
                    <div className={`
                      flex flex-wrap items-start justify-start gap-1
                    `}
                    >
                      <div className={`
                        flex h-6 items-center justify-center rounded-md
                        bg-[#1c54a5]/20 px-2 py-1
                      `}
                      >
                        <p className={`
                          text-left text-xs font-medium text-[#1c54a5]
                        `}
                        >
                          {formatDistanceToNow(new Date(item.created_at))}
                          {' '}
                          ago
                        </p>
                      </div>
                    </div>

                    {/* Test Button - Only show for completed knowledge bases */}
                    {item.status === 'COMPLETED' && onTestKnowledgeBase && (
                      <Button
                        color="primary"
                        variant="flat"
                        size="sm"
                        className="flex-shrink-0"
                        onPress={() => onTestKnowledgeBase(item)}
                        startContent={<MessageCircle className="h-3 w-3" />}
                      >
                        Test
                      </Button>
                    )}
                  </div>
                </div>
              </CardBody>
            </Card>
          )
        })}
      </div>

      {openEdit && (
        <EditKnowledgeBase
          onSuccess={onRefresh}
          knowledgeBase={knowledgeBase}
          open={openEdit}
          setOpen={setOpenEdit}
        />
      )}
      {openDelete && (
        <DeleteKnowledgeBase
          onSuccess={onRefresh}
          knowledgeBase={knowledgeBase}
          open={openDelete}
          setOpen={setOpenDelete}
        />
      )}
    </div>
  )
}
